import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { useDialog, useLegacyEffect } from "helper/customHook";
import { DIALOG } from "helper/enum/dialogEnum";
import { isOpenDialogSelector } from "redux-store/static/redux/selector";
import { useDispatch, useSelector } from "react-redux";
import pxToRem from "theme/base/pxToRem";
import { ReactComponent as IconCopy } from "assets/svg/20-copy.svg";
import { ReactComponent as IconEdit } from "assets/svg/20-edit.svg";

import {
  employeeLeaveDetailSelector,
  employeeLeaveDetailStatus,
} from "modules/DeclareLeave/redux/selector";
import { ReactComponent as IconClose } from "assets/svg/20-close.svg";

import { formatDateStringToDDMMYYYY } from "helper/utils";
import {
  ApproveStatus,
  ApproveStatusValue,
  IApproveStatus,
  LeaveType,
  statusColor,
  TitleContent,
} from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/const";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  DialogTitleCustom,
  Title,
} from "modules/PersonnelInformation/Pages/Main/component/modal/style";

import EmptyState from "components/common/EmptyState";
import { ReduxStateType } from "redux-store/types";
import { declareLeaveURL } from "modules/DeclareLeave/Pages/Main/DeclareLeaveContent";
import isString from "lodash/isString";
import { accountInfoDataSelector } from "layouts/Sidenav/redux/selector";
import { DialogActionWrapper } from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/style";
import DocForm from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/components/ListDocument";
import {
  getEmployeeLeaveDetailStart,
  resetEmployeeLeaveDetail,
} from "modules/DeclareLeave/redux/getEmployeeLeaveDetail";
import {
  setIsUpdateLeaveMaternityOrder,
  updateTimeListConfig,
} from "modules/DeclareLeave/redux/updateLeaveMaternityOrder";
import { setRequestLeaveDetailId } from "modules/DeclareLeave/redux/getRequestLeaveDetail";

const LeaveRequestDetails = () => {
  const { tab, id: idViewDetail } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { employeeCode } = useSelector(accountInfoDataSelector);

  const dispatch = useDispatch();
  const data = useSelector(employeeLeaveDetailSelector);
  const status = useSelector(employeeLeaveDetailStatus);

  const requestLeaveDialog = useDialog(DIALOG.EMPLOYEE_REQUEST_LEAVE_DETAIL);
  const open = useSelector(
    isOpenDialogSelector(DIALOG.EMPLOYEE_REQUEST_LEAVE_DETAIL)
  );
  const confirmEditDialog = useDialog(DIALOG.CONFIRM_EDIT);

  useLegacyEffect(() => {
    if (idViewDetail) {
      dispatch(getEmployeeLeaveDetailStart({ id: idViewDetail }));
      requestLeaveDialog.open();
    }
    // return () => {
    //   dispatch(setDeleteRequestData(""));
    // };
  }, [idViewDetail]);

  const renderBoxContent = (
    title: string,
    content: string | number,
    hasDivider?: boolean,
    fontWeightContent?: 400 | 500 | 600
  ) => (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      sx={(theme) => ({
        paddingBottom: 1.5,
        borderBottom: hasDivider
          ? `1px solid ${theme.palette.stroke.strokeDefault2}`
          : "none",
        marginBottom: hasDivider ? 1.5 : 0,
      })}
    >
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.size.sm_14,
          color: theme.palette.text.textSecondary,
          fontWeight: theme.typography.fw_regular_400,
        })}
      >
        {title}
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.size.sm_14,
          color:
            title === TitleContent.status
              ? statusColor(theme, content as IApproveStatus, "color")
              : theme.palette.text.textPrimary,
          fontWeight: fontWeightContent ?? theme.typography.fw_regular_400,
        })}
      >
        {title === TitleContent.status ? ApproveStatus[content] : content}
      </Typography>
    </Box>
  );

  const renderLeaveDetails = () =>
    (data?.details ?? []).map((leave, index) => {
      const listLeave = data?.details ?? [];
      const date =
        data?.type === LeaveType.SHORT_TERM
          ? `Ngày ${formatDateStringToDDMMYYYY(leave.fromDate)}`
          : `Từ ${formatDateStringToDDMMYYYY(listLeave[0]?.fromDate)} đến ${formatDateStringToDDMMYYYY(listLeave[listLeave.length - 1]?.toDate)}`;
      if (
        data?.type === LeaveType.SHORT_TERM ||
        (data?.type === LeaveType.LONG_TERM && index === 0)
      ) {
        const hasBorderBottom =
          data?.type === LeaveType.SHORT_TERM && index < listLeave.length - 1;
        return (
          <Box
            key={index}
            sx={(theme) => ({
              display: "flex",
              justifyContent: "space-between",
              paddingY: 1.5,
              borderBottom: hasBorderBottom
                ? `1px solid ${theme.palette.stroke.strokeDefault}`
                : "none",
            })}
          >
            <Box display={"flex"} gap={1.5}>
              <Typography fontSize={14}>{date}</Typography>

              {tab === "order-management" && (
                <Typography
                  fontSize={14}
                  fontWeight={400}
                  sx={(theme) => ({
                    color: theme.palette.text.textSecondary,
                    borderLeft: `1px solid ${theme.palette.stroke.strokeDefault}`,
                    paddingLeft: 1.5,
                  })}
                >
                  {leave.leaveHalfDay ? "Nghỉ nửa ngày" : "Nghỉ cả ngày"}
                </Typography>
              )}
            </Box>
            <Typography
              fontSize={14}
              fontWeight={600}
              sx={{
                color: leave.hexColor,
                textDecoration: "underline",
                textDecorationStyle: "dotted",
                textDecorationColor: leave.hexColor,
              }}
            >
              {leave.reasonName}
            </Typography>
          </Box>
        );
      }
      return null;
    });

  const renderNote = (status: IApproveStatus, note: string) => {
    if (status === ApproveStatusValue.PENDING || !note) return null;
    return renderBoxContent(
      status === ApproveStatusValue.REFUSE ? "Lý do từ chối" : "Ghi chú",
      note
    );
  };

  const handleCopyLink = () => {
    const linkToCopy = window.location.href.replace(
      declareLeaveURL.employeeOrderLeave,
      declareLeaveURL.orderManagement
    );
    navigator.clipboard
      .writeText(linkToCopy)
      .then(() => {
        toast.success("Đã sao chép liên kết");
      })
      .catch(() => {
        toast.error("Sao chép liên kết thất bại");
      });
  };

  const closeModal = () => {
    requestLeaveDialog.close();
    const newURL = pathname.replace(`/${idViewDetail}`, "");
    navigate(newURL, { replace: true });
  };

  const onClickEdit = () => {
    const month = data.details[0].fromDate.split("-")[1];
    const year = data.details[0].fromDate.split("-")[0];

    confirmEditDialog.open();
    dispatch(
      updateTimeListConfig({
        month,
        year,
        employeeCode: data.employeeCode,
      })
    );
    dispatch(setIsUpdateLeaveMaternityOrder(true));
    dispatch(setRequestLeaveDetailId(idViewDetail));
    closeModal();
  };

  const handleClose = () => {
    dispatch(resetEmployeeLeaveDetail());
    closeModal();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitleCustom>
        <Title>Chi tiết đơn xin nghỉ</Title>
        <IconButton onClick={handleClose}>
          <IconClose />
        </IconButton>
      </DialogTitleCustom>
      {isString(data) && status !== ReduxStateType.INIT && (
        <DialogContent>
          <EmptyState message={data} />
        </DialogContent>
      )}
      {status === ReduxStateType.SUCCESS && (
        <>
          <DialogContent
            sx={(theme) => ({
              background: theme.palette.background.bgTertiary,
              p: 0,
            })}
          >
            <Box
              sx={(theme) => ({
                margin: 2,
                padding: pxToRem(20),
                background: "white",
                borderRadius: pxToRem(8),
              })}
            >
              <Box mb={2}>
                <Typography gutterBottom>
                  Ngày tạo đơn:{" "}
                  {formatDateStringToDDMMYYYY(data?.registerDate ?? "")}
                </Typography>
              </Box>
              {(data?.relatedPersons ?? [])
                .map((i) => i.value)
                .includes(employeeCode) && (
                <>
                  {renderBoxContent(
                    TitleContent.employeeName,
                    data?.employeeName
                  )}
                  {renderBoxContent(
                    "",
                    `${data?.positionName} - ${data?.departmentName || data?.departmentLevel1Name || ""}`
                  )}
                </>
              )}
              <Box mt={2} mb={pxToRem(20)}>
                <Typography
                  sx={(theme) => ({
                    fontSize: theme.typography.size.sm_14,
                    color: theme.palette.text.textSecondary,
                    fontWeight: theme.typography.fw_regular_400,
                  })}
                  mb={1}
                >
                  Thông tin ngày nghỉ
                </Typography>
                <Box
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.stroke.strokeDefault}`,
                    borderRadius: pxToRem(8),
                    paddingX: 2,
                    paddingY: 0.5,
                  })}
                >
                  {renderLeaveDetails()}
                </Box>
              </Box>
              {renderBoxContent(
                TitleContent.totalLeave,
                `${data?.totalLeaveDay ?? 0} ngày`
              )}
              {renderBoxContent(TitleContent.leaveReason, data?.leaveReason)}
              {renderBoxContent(
                TitleContent.relatedPerson,
                (data?.relatedPersons ?? []).map((i) => i.label).join(", "),
                true
              )}

              {data?.approvals?.map((approval, index) => (
                <>
                  {renderBoxContent(
                    `${TitleContent.approval} (${index + 1})`,
                    `${approval.approverName} - ${ApproveStatus[approval.approveStatus]}`
                  )}
                  {renderNote(approval.approveStatus, approval.refuseReason)}
                </>
              ))}
              {renderBoxContent(
                TitleContent.status,
                data?.finalApproveStatus,
                false,
                600
              )}

              {renderBoxContent(
                TitleContent.approvalDate,
                data?.finalApproveDate
                  ? formatDateStringToDDMMYYYY(data?.finalApproveDate)
                  : "---"
              )}
              {data?.note && renderBoxContent("Ghi chú", data?.note)}
              {(data?.attachedFiles ?? []).length > 0 && (
                <Box mb={1}>
                  <Typography
                    sx={(theme) => ({
                      fontSize: theme.typography.size.sm_14,
                      color: theme.palette.text.textSecondary,
                      fontWeight: theme.typography.fw_regular_400,
                    })}
                  >
                    File đính kèm
                  </Typography>
                  <Box display={"flex"} maxWidth={"inherit"} overflow={"auto"}>
                    {(data?.attachedFiles ?? []).map((document, index) => (
                      <DocForm key={index} content={document} />
                    ))}
                  </Box>
                </Box>
              )}

              <Button
                variant="outlined"
                color="primary"
                sx={{ width: "100%" }}
                onClick={handleCopyLink}
              >
                <IconCopy />
                <Typography
                  sx={(theme) => ({
                    color: theme.palette.text.textLink,
                    fontWeight: theme.typography.fw_semibold_600,
                    ml: 1,
                  })}
                >
                  Sao chép liên kết
                </Typography>
              </Button>
            </Box>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
            <DialogActionWrapper>
              <Button
                variant="blur"
                fullWidth
                sx={(theme) => ({
                  mr: 1,
                  background: theme.palette.background.bgSecondary,
                  color: theme.palette.text.textPrimary,
                })}
                onClick={handleClose}
              >
                Đóng
              </Button>
              <Button
                variant="blur"
                color="primary"
                fullWidth
                disabled={!data?.editable}
                onClick={onClickEdit}
                sx={(theme) => ({
                  fontSize: pxToRem(14),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: pxToRem(2),
                })}
              >
                <IconEdit />
                <span>Chỉnh sửa</span>
              </Button>
            </DialogActionWrapper>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default LeaveRequestDetails;
